import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API, getToken } from "@/lib/api";
import { formatDate, formatEUR } from "@/lib/constants";
import { useTableSort } from "@/lib/useTableSort";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, ArrowLeft, Warning, CheckCircle, Paperclip, Trash, Upload, FileText, Receipt,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function NebenkostenDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const [property, setProperty] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [docs, setDocs] = useState([]);
  const [addPosOpen, setAddPosOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/nk-billings/${id}`);
    setBilling(data);
    if (data.property_id) {
      const p = await api.get(`/properties/${data.property_id}`);
      setProperty(p.data);
    }
    // Re-fetch documents so newly uploaded attachments appear immediately
    try {
      const r = await api.get("/documents", { params: { limit: 500 } });
      setDocs(r.data);
    } catch (_) { /* ignore */ }
  };
  useEffect(() => {
    load();
    api.get("/billing-schemas/catalog").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/documents", { params: { limit: 500 } }).then((r) => setDocs(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  if (!billing || !catalog) return <div className="p-6 text-slate-500">Lade…</div>;

  const positions = billing.positions || [];
  const missing = positions.filter((p) => !p.attachment_doc_id);
  const issued = billing.status === "issued";
  const canIssue = positions.length > 0 && missing.length === 0 && !issued;

  const issue = async () => {
    try {
      await api.post(`/nk-billings/${id}/issue`);
      toast.success("Abrechnung freigegeben");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Freigabe nicht möglich");
    }
  };

  const remove = async () => {
    if (!await window._domivusConfirm("Abrechnung wirklich löschen?")) return;
    try {
      await api.delete(`/nk-billings/${id}`);
      toast.success("Abrechnung gelöscht");
      navigate(-1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <div className="space-y-4" data-testid="nebenkosten-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft size={16} /></Button>
        <div className="flex-1">
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">{billing.title}</h1>
          <p className="text-xs text-slate-500">
            {property?.name} · {formatDate(billing.period_from)} – {formatDate(billing.period_to)} · Schema: {billing.schema_name_snapshot}
          </p>
        </div>
        <StatusBadge status={billing.status} />
        {!issued && (
          <>
            <Button onClick={issue} disabled={!canIssue}
              className={canIssue ? "bg-emerald-700 hover:bg-emerald-800" : "bg-slate-200 text-slate-400"}
              title={canIssue ? "Freigeben" : (missing.length > 0 ? `${missing.length} Position(en) ohne Anlage` : "Keine Positionen")}
              data-testid="issue-btn">
              <CheckCircle size={14} className="mr-1.5" /> Freigeben
            </Button>
            <Button variant="ghost" size="icon" onClick={remove} className="hover:text-rose-700" data-testid="delete-btn"><Trash size={14} /></Button>
          </>
        )}
      </div>

      {missing.length > 0 && !issued && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="missing-anlagen-banner">
          <Warning size={18} weight="fill" className="shrink-0 mt-0.5" />
          <div>
            <strong>{missing.length} Position{missing.length !== 1 ? "en" : ""} ohne Anlage.</strong>{" "}
            Die Abrechnung kann erst freigegeben werden, wenn jeder Position eine Anlage (Rechnung/Beleg) zugeordnet ist.
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold tracking-tight text-slate-900">Positionen ({positions.length})</h2>
          {!issued && (
            <Dialog open={addPosOpen} onOpenChange={setAddPosOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1" data-testid="add-position-btn">
                  <Plus size={14} /> Position hinzufügen
                </Button>
              </DialogTrigger>
              <AddPositionDialog
                billing={billing}
                catalog={catalog}
                onSaved={() => { setAddPosOpen(false); load(); }}
              />
            </Dialog>
          )}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <Th>Pos</Th>
              <Th>Kostenart</Th>
              <Th className="text-right">Gesamt</Th>
              <Th>Verteiler</Th>
              <Th>Anlage</Th>
              {!issued && <Th className="w-20 text-right"></Th>}
            </tr>
          </thead>
          <tbody>
            {positions.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-500">Noch keine Positionen.</td></tr>
            )}
            {positions.map((p) => (
              <PositionRow key={p.id} billing={billing} position={p} docs={docs} issued={issued} catalog={catalog} onChanged={load} />
            ))}
          </tbody>
        </table>
      </div>

      <TenantSharesSection billing={billing} docs={docs} onReload={load} />
    </div>
  );
}

function TenantSharesSection({ billing, docs, onReload }) {
  const [tenancies, setTenancies] = useState([]);
  const [busy, setBusy] = useState(false);
  const [busyTid, setBusyTid] = useState(null);

  useEffect(() => {
    api.get(`/nk-billings/${billing.id}/eligible-tenancies`)
      .then((r) => setTenancies(r.data)).catch(() => {});
  }, [billing.id]);

  const generateOne = async (tid) => {
    setBusyTid(tid);
    try {
      const { data } = await api.post(`/nk-billings/${billing.id}/generate-pdf`, { tenancy_id: tid });
      toast.success(`PDF erstellt · ${data.balance >= 0 ? "Nachzahlung" : "Guthaben"} ${formatEUR(Math.abs(data.balance))}`);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF-Erstellung fehlgeschlagen");
    } finally {
      setBusyTid(null);
    }
  };

  const generateBatch = async () => {
    if (!await window._domivusConfirm(`PDFs für alle ${tenancies.length} Mietverhältnisse erstellen?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/nk-billings/${billing.id}/generate-pdfs`);
      const ok = (data.results || []).filter((r) => r.status === "ok").length;
      const err = (data.results || []).filter((r) => r.status === "error").length;
      toast.success(`Stapelabfertigung abgeschlossen: ${ok} erstellt, ${err} fehlgeschlagen`);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stapelverarbeitung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  // Find existing generated PDFs by linked_nk_billing_id + linked_tenancy_id
  const findPdfDoc = (tid) =>
    [...docs].reverse().find((d) => d.linked_nk_billing_id === billing.id && d.linked_tenancy_id === tid);

  if (tenancies.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-md p-4 text-sm text-slate-500 italic" data-testid="tenant-shares-empty">
        Keine aktiven Mietverhältnisse im Abrechnungszeitraum.
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="tenant-shares-section">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-semibold tracking-tight text-slate-900">Abrechnungen pro Mieter ({tenancies.length})</h2>
          <p className="text-xs text-slate-500 mt-0.5">Anteil = Gesamtbetrag × Verteilerschlüssel-Quote · Vorauszahlungen aus Mietkonto-Buchungen.</p>
        </div>
        <Button
          onClick={generateBatch}
          disabled={busy || (billing.positions || []).length === 0}
          className="bg-slate-900 hover:bg-slate-800 gap-1"
          data-testid="batch-pdf-btn"
        >
          <FileText size={14} /> {busy ? "Erstelle…" : "Stapel: Alle PDFs erstellen"}
        </Button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left">
          <tr>
            <Th>Vertrag</Th>
            <Th>Mieter</Th>
            <Th>Zeitraum</Th>
            <Th className="text-right">PDF</Th>
            <Th className="w-32 text-right"></Th>
          </tr>
        </thead>
        <tbody>
          {tenancies.map((t) => {
            const doc = findPdfDoc(t.id);
            return (
              <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`tenant-share-row-${t.id}`}>
                <Td className="font-mono text-xs">{t.contract_number}</Td>
                <Td>{(t.tenant_names || []).join(", ") || "—"}</Td>
                <Td className="text-xs text-slate-600">
                  {formatDate(t.contract_start)} – {t.contract_end ? formatDate(t.contract_end) : "unbefristet"}
                </Td>
                <Td className="text-right">
                  {doc ? (
                    <a
                      href={`${API}/documents/${doc.id}/download?token=${getToken()}`}
                      target="_blank" rel="noreferrer"
                      className="text-emerald-700 hover:underline inline-flex items-center gap-1 text-xs"
                      data-testid={`tenant-pdf-link-${t.id}`}
                    >
                      <FileText size={12} /> {doc.original_filename}
                    </a>
                  ) : (
                    <span className="text-xs text-slate-400 italic">Noch keine</span>
                  )}
                </Td>
                <Td className="text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => generateOne(t.id)}
                    disabled={busyTid === t.id || busy || (billing.positions || []).length === 0}
                    data-testid={`tenant-pdf-btn-${t.id}`}
                    className="gap-1 text-xs"
                  >
                    <Receipt size={12} /> {busyTid === t.id ? "…" : doc ? "Neu erstellen" : "PDF erstellen"}
                  </Button>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PositionRow({ billing, position, docs, issued, catalog, onChanged }) {
  const [attachOpen, setAttachOpen] = useState(false);
  const linkedDoc = docs.find((d) => d.id === position.attachment_doc_id);

  const detachAttachment = async () => {
    try {
      await api.delete(`/nk-billings/${billing.id}/positions/${position.id}/attachment`);
      toast.success("Anlage entfernt");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const removePos = async () => {
    if (!await window._domivusConfirm("Position löschen?")) return;
    try {
      await api.delete(`/nk-billings/${billing.id}/positions/${position.id}`);
      toast.success("Position gelöscht");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const distLabel = catalog?.distribution_labels?.[position.distribution_key] || position.distribution_key;

  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`position-row-${position.id}`}>
      <Td className="font-mono text-slate-900">{position.code}</Td>
      <Td>
        <div className="font-medium text-slate-900">{position.label}</div>
        {position.notes && <div className="text-xs text-slate-500 mt-0.5">{position.notes}</div>}
      </Td>
      <Td className="text-right tabular-nums">{formatEUR(position.total_amount)}</Td>
      <Td className="text-xs text-slate-700">{distLabel}</Td>
      <Td>
        {linkedDoc ? (
          <div className="flex items-center gap-2 text-xs">
            <a
              href={`${API}/documents/${linkedDoc.id}/download?token=${getToken()}`}
              target="_blank" rel="noreferrer"
              className="text-emerald-700 hover:underline inline-flex items-center gap-1"
              data-testid={`position-doc-link-${position.id}`}
            >
              <Paperclip size={12} /> {linkedDoc.title || linkedDoc.original_filename}
            </a>
            {!issued && (
              <button onClick={detachAttachment} className="text-slate-400 hover:text-rose-600" title="Anlage entfernen">
                <Trash size={11} />
              </button>
            )}
          </div>
        ) : (
          <Dialog open={attachOpen} onOpenChange={setAttachOpen}>
            <DialogTrigger asChild>
              <button
                disabled={issued}
                className="inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 disabled:opacity-50"
                data-testid={`attach-btn-${position.id}`}
              >
                <Warning size={12} weight="fill" /> Anlage fehlt – jetzt zuordnen
              </button>
            </DialogTrigger>
            <AttachDialog
              billing={billing} position={position} docs={docs}
              onSaved={() => { setAttachOpen(false); onChanged(); }}
            />
          </Dialog>
        )}
      </Td>
      {!issued && (
        <Td className="text-right">
          <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-rose-700" onClick={removePos} data-testid={`del-position-${position.id}`}>
            <Trash size={14} />
          </Button>
        </Td>
      )}
    </tr>
  );
}

function AddPositionDialog({ billing, catalog, onSaved }) {
  const [code, setCode] = useState("");
  const [net, setNet] = useState("");
  const [vat, setVat] = useState("19");
  const [brutto, setBrutto] = useState("");
  const [bruttoMode, setBruttoMode] = useState(false); // false = aus Netto+USt berechnen
  const [distribution, setDistribution] = useState("");
  const [notes, setNotes] = useState("");
  const [remark, setRemark] = useState("");
  const [assignmentType, setAssignmentType] = useState("house"); // house|tenancies
  const [assignedTenancies, setAssignedTenancies] = useState([]);
  const [issuer, setIssuer] = useState("");
  const [tenancies, setTenancies] = useState([]);
  const [contacts, setContacts] = useState([]);

  const positions = catalog.positions || [];
  const selectedCat = positions.find((p) => p.code === code);

  useEffect(() => {
    if (selectedCat) setDistribution(selectedCat.default_distribution);
  }, [selectedCat]);

  useEffect(() => {
    api.get(`/nk-billings/${billing.id}/eligible-tenancies`).then((r) => setTenancies(r.data)).catch(() => {});
    api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {});
  }, [billing.id]);

  // Brutto-Berechnung
  useEffect(() => {
    if (!bruttoMode) {
      const n = parseFloat(net) || 0;
      const v = parseFloat(vat) || 0;
      setBrutto((n * (1 + v / 100)).toFixed(2));
    }
  }, [net, vat, bruttoMode]);

  const onBruttoChange = (val) => {
    setBruttoMode(true);
    setBrutto(val);
    const b = parseFloat(val) || 0;
    const v = parseFloat(vat) || 0;
    if (v > 0) setNet((b / (1 + v / 100)).toFixed(2));
    else setNet(b.toFixed(2));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!code || (!net && !brutto)) { toast.error("Position und mind. ein Betrag erforderlich"); return; }
    if (assignmentType === "tenancies" && assignedTenancies.length === 0) {
      toast.error("Bitte mindestens einen Mieter auswählen oder auf Haus umstellen");
      return;
    }
    try {
      await api.post(`/nk-billings/${billing.id}/positions`, {
        code,
        net_amount: Number(net) || 0,
        vat_rate: Number(vat) || 0,
        total_amount: Number(brutto) || 0,
        distribution_key: distribution,
        notes: notes || null,
        remark: remark || null,
        assignment_type: assignmentType,
        assigned_tenancy_ids: assignmentType === "tenancies" ? assignedTenancies : [],
        invoice_issuer_contact_id: issuer || null,
      });
      toast.success("Position hinzugefügt");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Position hinzufügen</DialogTitle>
        <DialogDescription>Beträge intern als Netto + USt. erfassen; auf der Abrechnung wird der Brutto-Betrag ausgewiesen.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label className="text-xs text-slate-700">Position aus Schema</Label>
          <Select value={code} onValueChange={setCode}>
            <SelectTrigger data-testid="position-code-select"><SelectValue placeholder="Position auswählen" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {positions.map((p) => (
                <SelectItem key={p.code} value={p.code}>{p.code} · {p.label}{p.group ? ` (${p.group})` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedCat?.hint && <div className="text-xs text-amber-700 mt-1 italic">{selectedCat.hint}</div>}
        </div>

        <div className="grid grid-cols-3 gap-3 bg-slate-50/60 border border-slate-200 rounded-md p-3">
          <div>
            <Label className="text-xs text-slate-700">Netto (€)</Label>
            <Input type="number" step="0.01" value={net} onChange={(e) => { setBruttoMode(false); setNet(e.target.value); }} data-testid="position-net" />
          </div>
          <div>
            <Label className="text-xs text-slate-700">USt. (%)</Label>
            <Input type="number" step="0.1" value={vat} onChange={(e) => setVat(e.target.value)} data-testid="position-vat" />
          </div>
          <div>
            <Label className="text-xs text-slate-700">Brutto (€)</Label>
            <Input type="number" step="0.01" value={brutto} onChange={(e) => onBruttoChange(e.target.value)} data-testid="position-brutto"
              className="font-semibold" />
          </div>
        </div>

        {selectedCat && (
          <div>
            <Label className="text-xs text-slate-700">Verteilerschlüssel</Label>
            <Select value={distribution} onValueChange={setDistribution}>
              <SelectTrigger data-testid="position-distribution"><SelectValue /></SelectTrigger>
              <SelectContent>
                {selectedCat.allowed_distributions.map((d) => (
                  <SelectItem key={d} value={d}>{catalog.distribution_labels[d] || d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div>
          <Label className="text-xs text-slate-700">Zuordnung</Label>
          <div className="flex gap-2 mt-1" data-testid="position-assignment-toggle">
            <button type="button"
              onClick={() => setAssignmentType("house")}
              className={`px-3 py-1.5 rounded-sm text-sm border ${assignmentType === "house" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}
              data-testid="assignment-house"
            >Gesamtes Haus</button>
            <button type="button"
              onClick={() => setAssignmentType("tenancies")}
              className={`px-3 py-1.5 rounded-sm text-sm border ${assignmentType === "tenancies" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}
              data-testid="assignment-tenancies"
            >Nur bestimmte Mieter</button>
          </div>
          {assignmentType === "tenancies" && (
            <div className="mt-2 border border-slate-200 rounded-sm max-h-40 overflow-y-auto bg-white" data-testid="tenancy-picker">
              {tenancies.length === 0 && <div className="p-2 text-xs text-slate-500 italic">Keine Mietverhältnisse im Zeitraum.</div>}
              {tenancies.map((t) => {
                const active = assignedTenancies.includes(t.id);
                return (
                  <button key={t.id} type="button"
                    onClick={() => setAssignedTenancies(active
                      ? assignedTenancies.filter((x) => x !== t.id)
                      : [...assignedTenancies, t.id])}
                    className={`w-full text-left px-3 py-1.5 text-sm border-b border-slate-100 last:border-b-0 hover:bg-slate-50 ${active ? "bg-emerald-50" : ""}`}
                    data-testid={`tenancy-pick-${t.id}`}
                  >
                    <span className="font-mono text-xs text-slate-500 mr-2">{t.contract_number}</span>
                    {(t.tenant_names || []).join(", ") || "—"}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <Label className="text-xs text-slate-700">Rechnungssteller</Label>
          <IssuerSearchSelect contacts={contacts} value={issuer} onChange={setIssuer} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-700">Interne Notiz</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="position-notes" />
          </div>
          <div>
            <Label className="text-xs text-slate-700">Bemerkung (erscheint auf Abrechnung)</Label>
            <Textarea rows={2} value={remark} onChange={(e) => setRemark(e.target.value)} data-testid="position-remark" />
          </div>
        </div>

        <DialogFooter>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800 domivus-primary" data-accent-target="primary" data-testid="position-save-btn">Hinzufügen</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function AttachDialog({ billing, position, docs, onSaved }) {
  const [docId, setDocId] = useState("");
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return docs.slice(0, 200);
    return docs
      .filter((d) => (d.title || "").toLowerCase().includes(term) || (d.original_filename || "").toLowerCase().includes(term))
      .slice(0, 200);
  }, [docs, search]);

  const attach = async () => {
    if (!docId) { toast.error("Bitte ein Dokument auswählen"); return; }
    try {
      await api.post(`/nk-billings/${billing.id}/positions/${position.id}/attachment`, { document_id: docId });
      toast.success("Anlage zugeordnet");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const uploadAndAttach = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", `Anlage ${position.code} ${position.label}`);
      fd.append("category", "Nebenkosten");
      fd.append("direction", "in");
      const up = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/nk-billings/${billing.id}/positions/${position.id}/attachment`, { document_id: up.data.id });
      toast.success("Anlage hochgeladen und zugeordnet");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>Anlage zuordnen</DialogTitle>
        <DialogDescription>
          Wählen Sie ein bereits hochgeladenes Dokument oder laden Sie direkt eine neue Datei hoch.
          Für Position <strong className="font-mono">{position.code}</strong> — {position.label}.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3">
        <div>
          <Label className="text-xs text-slate-700">Neue Datei hochladen</Label>
          <div className="mt-1 flex gap-2">
            <input
              type="file" ref={fileRef} className="hidden"
              accept="application/pdf,image/*"
              onChange={(e) => uploadAndAttach(e.target.files?.[0])}
              data-testid="attach-upload-input"
            />
            <Button type="button" variant="outline" className="gap-1 flex-1"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              data-testid="attach-upload-btn">
              <Upload size={14} /> {uploading ? "Lade hoch…" : "Datei wählen und hochladen"}
            </Button>
          </div>
        </div>

        <div className="border-t border-slate-100 pt-3">
          <Label className="text-xs text-slate-700">Oder bestehendes Dokument zuordnen</Label>
          <Input
            placeholder="Suchen nach Titel oder Dateiname"
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="mt-1 mb-2"
          />
          <div className="border border-slate-200 rounded-sm max-h-56 overflow-y-auto">
            {filtered.length === 0 && <div className="p-3 text-xs text-slate-500 italic">Keine Treffer</div>}
            {filtered.map((d) => (
              <button
                key={d.id} type="button"
                onClick={() => setDocId(d.id)}
                className={`w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-slate-50 text-sm ${docId === d.id ? "bg-emerald-50" : ""}`}
                data-testid={`attach-doc-pick-${d.id}`}
              >
                <div className="font-medium text-slate-900 truncate flex items-center gap-2">
                  <FileText size={12} className="text-slate-400" />
                  {d.title || d.original_filename}
                </div>
                <div className="text-xs text-slate-500">{d.category} · {formatDate(d.created_at)}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button onClick={attach} disabled={!docId} className="bg-slate-900 hover:bg-slate-800" data-testid="attach-confirm-btn">
          <Paperclip size={14} className="mr-1.5" /> Zuordnen
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function IssuerSearchSelect({ contacts, value, onChange }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const selected = contacts.find((c) => c.id === value);
  const displayName = (c) => {
    if (!c) return "";
    const person = c.first_name && c.last_name ? `${c.first_name} ${c.last_name}` : c.last_name;
    return [c.company_name, person].filter(Boolean).join(" · ") || "—";
  };
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return contacts.slice(0, 100);
    return contacts.filter((c) =>
      displayName(c).toLowerCase().includes(term)
      || (c.email || "").toLowerCase().includes(term)
      || (c.phone || "").toLowerCase().includes(term)
    ).slice(0, 100);
  }, [contacts, search]);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full h-9 px-3 text-left text-sm border border-slate-200 rounded-md bg-white hover:bg-slate-50 flex items-center justify-between"
        data-testid="position-issuer"
      >
        <span className={selected ? "text-slate-900" : "text-slate-400"}>
          {selected ? displayName(selected) : "Aus Kontakten wählen…"}
        </span>
        <span className="text-xs text-slate-400">▾</span>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-md max-h-72 overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <Input
              autoFocus
              placeholder="Suchen nach Name, E-Mail, Telefon…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              data-testid="position-issuer-search"
              className="h-8 text-sm"
            />
          </div>
          <div className="overflow-y-auto max-h-56">
            {value && (
              <button type="button"
                onClick={() => { onChange(""); setOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-xs text-slate-500 italic hover:bg-slate-50"
              >
                Auswahl entfernen
              </button>
            )}
            {filtered.length === 0 && (
              <div className="p-3 text-xs text-slate-500 italic">Keine Treffer</div>
            )}
            {filtered.map((c) => (
              <button key={c.id} type="button"
                onClick={() => { onChange(c.id); setOpen(false); setSearch(""); }}
                className={`w-full text-left px-3 py-2 border-b border-slate-50 hover:bg-slate-50 text-sm ${value === c.id ? "bg-emerald-50" : ""}`}
                data-testid={`issuer-pick-${c.id}`}
              >
                <div className="font-medium text-slate-900 truncate">{displayName(c)}</div>
                {(c.email || c.phone) && (
                  <div className="text-xs text-slate-500 truncate">{[c.email, c.phone].filter(Boolean).join(" · ")}</div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft: { label: "Entwurf", className: "bg-slate-100 text-slate-700" },
    ready: { label: "Bereit", className: "bg-amber-100 text-amber-800" },
    issued: { label: "Freigegeben", className: "bg-emerald-100 text-emerald-800" },
  };
  const s = map[status] || map.draft;
  return <span className={`text-xs px-3 py-1 rounded-full ${s.className}`}>{s.label}</span>;
}

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2.5 align-middle ${className}`}>{children}</td>;
}
